import {
  type Accessor,
  createEffect,
  createMemo,
  createSignal,
  on,
  onCleanup,
} from "solid-js";

/**
 * Cadenza della narrazione a step della prima ricerca (SPEC AC1/AC2): soglie
 * in ms dall'inizio della richiesta. Le etichette vivono nel componente
 * (`SearchLoadingSteps`) via `t()` — mai qui (niente i18n nei `.ts`).
 */
export const SEARCH_STEP_TIMES = [0, 4_000, 10_000, 16_000] as const;

/** Età della richiesta oltre la quale compare la riga di cortesia (AC2). */
export const LONG_WAIT_AT = 24_000;

/**
 * Gate anti-flash (AC3/D13): il contenuto della loading screen monta solo se
 * la richiesta è in volo da almeno questa soglia, valutata contro `startedAt` —
 * mai con un `setTimeout` cieco che ritardi il mount di per sé, così un remount
 * a richiesta vecchia mostra la schermata immediatamente.
 */
export const ANTI_FLASH_GATE_MS = 120;

/** Cadenza del tick di fondo: risolve gli step, non le soglie sotto i 250ms. */
const TICK_MS = 250;

/**
 * L'orologio della richiesta di prima ricerca: la narrazione è ancorata
 * all'età della richiesta (AC2), non al mount del componente. `startedAt` è un
 * accessor perché una nuova prima-ricerca (es. retry dopo un errore) deve
 * ri-ancorare l'orologio senza rimontare nulla.
 *
 * `elapsed` è sempre `Date.now() - startedAt()` al momento in cui `now` viene
 * rinfrescato — dal tick di fondo o dal one-shot del gate — mai un conteggio di
 * tick accumulati, così una scheda in background coi timer rallentati riflette
 * comunque il tempo reale al risveglio (E-4).
 */
export function useSearchRequestClock(startedAt: Accessor<number>) {
  const [now, setNow] = createSignal(Date.now());
  const interval = setInterval(() => setNow(Date.now()), TICK_MS);
  onCleanup(() => clearInterval(interval));

  // Il tick di fondo quantizza ogni soglia al suo passo: da solo, il gate
  // anti-flash scatterebbe di fatto a 250ms e i 120ms sarebbero codice morto —
  // ogni retry lascerebbe l'area vuota per un quarto di secondo dopo il click.
  // Questo one-shot non ritarda nulla: rinfresca `now` esattamente sul confine
  // del gate, così `elapsed` resta una differenza di timestamp e il gate viene
  // valutato al suo valore reale. Ri-armato a ogni cambio di `startedAt` (retry
  // post-errore), con l'`onCleanup` interno che disarma il precedente.
  createEffect(
    on(startedAt, (anchor) => {
      const remaining = anchor + ANTI_FLASH_GATE_MS - Date.now();
      if (remaining <= 0) return;
      const gate = setTimeout(() => setNow(Date.now()), remaining);
      onCleanup(() => clearTimeout(gate));
    }),
  );

  // Il cambio di `startedAt` ri-ancora subito, senza aspettare il tick: il
  // memo legge l'accessor, ma il "now" fermo tra due tick non deve produrre
  // un elapsed negativo.
  const elapsed = createMemo(() => Math.max(0, now() - startedAt()));

  const stepIndex = createMemo(() => {
    let index = 0;
    for (let i = 0; i < SEARCH_STEP_TIMES.length; i++) {
      if (elapsed() >= SEARCH_STEP_TIMES[i]) index = i;
    }
    return index;
  });

  const isLongWait = createMemo(() => elapsed() >= LONG_WAIT_AT);

  return { elapsed, stepIndex, isLongWait };
}
